/**
 * Lightweight toast queue for transient async-outcome announcements.
 *
 * Built for #572 Phase 3: when a row's Apply Fix / Revert command reaches
 * terminal status while the operator has scrolled away, fire a 5s top-right
 * toast so they get a signal without needing to scroll back. Generic enough
 * to be reused for other async outcomes (license-save, scan-dispatch, etc.).
 *
 * Why not a third-party lib: `sonner` / `react-hot-toast` are pure-toast deps
 * that would add ~5-10 KB gzipped and another peer-dep to keep in lockstep.
 * Our needs (FIFO stack, max-3, intent variants, auto-dismiss) fit in ~80
 * lines and stay consistent with the existing dark-mode + Tailwind conventions.
 *
 * Spec: ../__tests__/useToastQueue.spec.tsx
 */

import { AlertOctagon, AlertTriangle, CheckCircle2, Info, X } from 'lucide-react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

export type ToastIntent = 'success' | 'error' | 'warning' | 'info'

export interface ToastInput {
  intent: ToastIntent
  title: string
  description?: string
  /**
   * Auto-dismiss after this many ms. Default 5000. Pass 0 for sticky toasts
   * the caller will dismiss programmatically via `dismiss(id)`.
   */
  durationMs?: number
}

interface Toast extends Required<Pick<ToastInput, 'intent' | 'title'>> {
  id: string
  description?: string
  durationMs: number
}

interface ToastApi {
  toast: (input: ToastInput) => string
  dismiss: (id: string) => void
}

const ToastQueueContext = createContext<ToastApi | null>(null)

const MAX_VISIBLE = 3
const DEFAULT_DURATION_MS = 5000

const INTENT_STYLES: Record<ToastIntent, { Icon: typeof Info; container: string; iconClass: string }> = {
  success: {
    Icon: CheckCircle2,
    container:
      'border-green-200 bg-green-50 text-green-900 dark:border-green-900/40 dark:bg-green-900/20 dark:text-green-100',
    iconClass: 'text-green-600 dark:text-green-400',
  },
  error: {
    Icon: AlertOctagon,
    container: 'border-red-200 bg-red-50 text-red-900 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-100',
    iconClass: 'text-red-600 dark:text-red-400',
  },
  warning: {
    Icon: AlertTriangle,
    container:
      'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-100',
    iconClass: 'text-amber-600 dark:text-amber-400',
  },
  info: {
    Icon: Info,
    container:
      'border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-900/40 dark:bg-blue-900/20 dark:text-blue-100',
    iconClass: 'text-blue-600 dark:text-blue-400',
  },
}

export function ToastQueueProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const counterRef = useRef(0)

  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  const toast = useCallback((input: ToastInput): string => {
    counterRef.current += 1
    const id = `t-${counterRef.current}`
    const next: Toast = {
      id,
      intent: input.intent,
      title: input.title,
      description: input.description,
      durationMs: input.durationMs ?? DEFAULT_DURATION_MS,
    }

    setToasts(prev => {
      // FIFO cap — drop oldest when at MAX_VISIBLE.
      const trimmed = prev.length >= MAX_VISIBLE ? prev.slice(prev.length - MAX_VISIBLE + 1) : prev

      return [...trimmed, next]
    })

    return id
  }, [])

  const api = useMemo<ToastApi>(() => ({ toast, dismiss }), [toast, dismiss])

  return (
    <ToastQueueContext.Provider value={api}>
      {children}
      {/* Stack: fixed top-right, gap between toasts, max-w-sm so descriptions
          wrap without overflowing the viewport on narrow screens. From md up it
          also clears a docked assistant (`--assistant-dock-inset`, published by
          the dock while open), which would otherwise sit under the toasts — and
          its width is capped to what is left of the window beside the dock, a
          1rem gutter each side, so a 384px stack beside a 384px dock in a 768px
          window does not run off the left edge. */}
      <div
        aria-live="polite"
        aria-label="Notifications"
        className="pointer-events-none fixed right-4 top-4 z-50 flex w-full max-w-sm flex-col gap-2 md:right-[calc(1rem_+_var(--assistant-dock-inset,0px))] md:max-w-[min(24rem,calc(100vw_-_2rem_-_var(--assistant-dock-inset,0px)))]"
      >
        {toasts.map(t => (
          <ToastRow key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastQueueContext.Provider>
  )
}

function ToastRow({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  useEffect(() => {
    if (toast.durationMs === 0) return
    const t = setTimeout(onDismiss, toast.durationMs)

    return () => clearTimeout(t)
    // onDismiss is stable per render (parent's useCallback); deliberately
    // depend on the id so a different toast resets the timer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast.id, toast.durationMs])

  const { Icon, container, iconClass } = INTENT_STYLES[toast.intent]

  return (
    <div
      role="status"
      className={`pointer-events-auto flex items-start gap-3 rounded-md border px-3 py-2 text-sm shadow-md ${container}`}
    >
      <Icon aria-hidden className={`mt-0.5 size-4 shrink-0 ${iconClass}`} />
      <div className="flex-1 min-w-0">
        <p className="font-medium">{toast.title}</p>
        {toast.description && <p className="mt-0.5 text-xs opacity-80">{toast.description}</p>}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss notification"
        className="shrink-0 rounded p-0.5 opacity-60 hover:bg-black/5 hover:opacity-100 dark:hover:bg-white/10"
      >
        <X aria-hidden className="size-3.5" />
      </button>
    </div>
  )
}

// Fallback used when a consumer renders outside a ToastQueueProvider — e.g.
// component-level Vitest renders that don't wrap with <AppLayout>. Returns
// a silent no-op so pages stay testable in isolation without every spec
// needing to plumb the provider. Throwing here would force a 30-spec
// rewrite for zero user-facing benefit; the provider is always present in
// the real app via AppLayout's wrap.
const NOOP_TOAST_API: ToastApi = {
  toast: () => '',
  dismiss: () => {},
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastQueueContext)

  return ctx ?? NOOP_TOAST_API
}
